import { useReveal } from '@/hooks/useReveal';

const stats = [
  { icon: 'ri-user-add-line', value: '3.5x', label: 'More Qualified Leads', description: 'Average increase in lead capture within the first 90 days of using DGFlow.', color: 'from-primary-500 to-primary-400', bgColor: 'bg-primary-100' },
  { icon: 'ri-funds-line', value: '2.8x', label: 'Higher Sales Revenue', description: 'Businesses report nearly triple revenue growth after automating their WhatsApp sales process.', color: 'from-accent-500 to-accent-400', bgColor: 'bg-accent-100' },
  { icon: 'ri-heart-line', value: '85%', label: 'Customer Retention', description: 'Automated engagement keeps customers coming back — 85% retention rate across all industries.', color: 'from-rose-500 to-rose-400', bgColor: 'bg-rose-100' },
  { icon: 'ri-flashlight-line', value: '1 min', label: 'Average Response Time', description: 'AI responds to customer queries in under 1 minutes — 24/7, even on holidays.', color: 'from-amber-500 to-amber-400', bgColor: 'bg-amber-100' },
  { icon: 'ri-timer-line', value: '40+ hrs', label: 'Manual Work Saved Weekly', description: 'Automation eliminates 40+ hours of repetitive manual tasks every single week.', color: 'from-violet-500 to-violet-400', bgColor: 'bg-violet-100' },
  { icon: 'ri-rocket-line', value: '4.2x', label: 'Team Productivity', description: 'Teams using DGFlow are 4.2x more productive — focus on closing, not chasing.', color: 'from-accent-500 to-accent-400', bgColor: 'bg-cyan-100' },
];

export default function StatsSection() {
  const { ref, visible } = useReveal();

  return (
    <section className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, oklch(var(--foreground-900)) 1px, transparent 1px)', backgroundSize: '32px 32px' }}>
      </div>

      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold uppercase tracking-wider mb-5">
            Real Results, Real Numbers
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-5 leading-tight">
            Businesses Using DGFlow{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
              Don&apos;t Just Grow — They Transform
            </span>
          </h2>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Based on aggregated data from 1000+ businesses across 24 industries.
            These aren&apos;t projections — they&apos;re actual results.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {stats.map(stat => (
            <div
              key={stat.label}
              className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-7 hover:border-amber-200/50 transition-all duration-400 hover:-translate-y-1"
            >
              <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-400`}>
                <i className={`${stat.icon} text-xl text-foreground-700`}></i>
              </div>
              <p className={`text-4xl md:text-5xl font-bold font-heading bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}>
                {stat.value}
              </p>
              <p className="text-base font-bold font-heading text-foreground-950 mb-2">{stat.label}</p>
              <p className="text-sm text-foreground-500 leading-relaxed">{stat.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}